<template>
  <BaseDataTable
    :entries
    :columns
    :loading
    :paginator-info="paginatorInfo"
    search-disabled
    @search="updateSearchSettings"
  >
    <template #Active="{ row }">
      <WdsIcon
        name="checkmark-filled"
        :data-test="row.isActive ? `webhook-active-${row.id}` : `webhook-not-active-${row.id}`"
        :color="row.isActive ? '#047E43' : '#DFDFE6'"
      />
    </template>
    <template #Name="{ row }">
      <WdsText :text="row.title" />
      <WdsText :text="row.url" secondary />
    </template>
    <template #SubscribedEvents="{ row }">
      <WdsText :text="getSubscribedEventsLabel(row.subscribedEvents)" />
    </template>
    <template #Action="{ row }">
      <WdsMenu>
        <template #trigger>
          <WdsButtonIcon transparent-background :data-test="`open-menu-${row.id}`">
            <WdsIcon name="overflow-menu-horizontal" />
          </WdsButtonIcon>
        </template>
        <WdsMenuOption :data-test="`edit-webhook-${row.id}`" @click="openModal(row)">
          <WdsIcon :size="16" name="edit" />
          {{ $t('Modify') }}
        </WdsMenuOption>
        <WdsMenuOption
          theme="DESTRUCTIVE"
          :data-test="`delete-webhook-${row.id}`"
          @click="deleteModal(row)"
        >
          <WdsIcon :size="16" name="trash-can" />
          {{ $t('Delete') }}
        </WdsMenuOption>
      </WdsMenu>
    </template>
  </BaseDataTable>

  <teleport to="#modals">
    <WebhookModalUpdate v-model:show="showUpdate" :webhook="webhook" />
    <WebhookModalDelete v-model:show="showDelete" :webhook="webhook" />
  </teleport>
</template>

<script setup lang="ts">
import { WdsButtonIcon, WdsIcon, WdsMenu, WdsMenuOption, WdsText } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, reactive, Ref, ref } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, useFragment } from '@/gql'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { TdDate } from '@/components/BaseDataTable/Cells'
import WebhookModalUpdate from '@/components/Integrations/Webhooks/WebhookModalUpdate.vue'
import { WebhooksQuery } from '@/components/Integrations/Webhooks/graphql/queries'
import { WebhookFragment } from '@/components/Integrations/Webhooks/graphql/fragments'
import WebhookModalDelete from '@/components/Integrations/Webhooks/WebhookModalDelete.vue'
import { PaginatorInfoFragment } from '@/components/GraphQL/fragments/paginatorInfoFragment'
import { WebhookEventType } from '@/gql/graphql'
import {
  SUPPLIER_NULL_SUBSCRIPTION_EVENT_TYPES,
  SUPPLIER_WEBHOOK_EVENT_TYPES,
} from '@/components/Integrations/Webhooks/types'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const clientId = 'publicApi'

const { isRecruiter } = useAccountCapabilities()

const totalEventTypes = isRecruiter.value
  ? SUPPLIER_WEBHOOK_EVENT_TYPES.length
  : Object.values(WebhookEventType).length

const getSubscribedEventsLabel = (subscribedEvents: WebhookEventType[] | null): string => {
  if (subscribedEvents === null) {
    return isRecruiter.value
      ? `${SUPPLIER_NULL_SUBSCRIPTION_EVENT_TYPES.length} / ${totalEventTypes}`
      : $t('All events')
  }
  return `${subscribedEvents.length} / ${totalEventTypes}`
}

const columns = [
  {
    slot: 'Active',
    key: 'isActive',
    title: $t('Active'),
  },
  {
    slot: 'Name',
    key: 'title',
    title: $t('Name'),
  },
  {
    slot: 'SubscribedEvents',
    key: 'subscribedEvents',
    title: $t('Events'),
  },
  {
    key: 'createdAt',
    title: $t('Created'),
    renderUsing: {
      componentProps(row) {
        return {
          date: row.createdAt,
        }
      },
      component: TdDate,
    },
  },
  {
    key: 'id',
    title: $t('Action'),
    slot: 'Action',
  },
]

const showUpdate = ref(false)
const showDelete = ref(false)

const pagination = reactive({
  page: 1,
  first: 50,
})

interface BaseDataTableSearchSettings {
  page: number
  perPage: number
  search: string
}

const updateSearchSettings = (searchSettings: BaseDataTableSearchSettings) => {
  pagination.page = searchSettings.page + 1 // searchSettings page is zero indexed
  pagination.first = searchSettings.perPage
}

const webhook: Ref<null | FragmentType<typeof WebhookFragment>> = ref(null)
const { result, loading } = useQuery(
  WebhooksQuery,
  () => ({
    accounts: [currentAccount.globalId],
    first: pagination.first,
    page: pagination.page,
  }),
  { clientId },
)

const entries = computed(() => {
  if (loading.value) {
    return []
  }

  return result.value?.webhooks.data.map((hook) => useFragment(WebhookFragment, hook))
})

const paginatorInfo = computed(
  () => useFragment(PaginatorInfoFragment, result.value?.webhooks.paginatorInfo) ?? undefined,
)
const openModal = (hook: FragmentType<typeof WebhookFragment>) => {
  webhook.value = hook
  showUpdate.value = true
}

const deleteModal = (hook: FragmentType<typeof WebhookFragment>) => {
  webhook.value = hook
  showDelete.value = true
}
</script>

<style scoped lang="scss">
</style>
